import React, { useEffect, useMemo } from "react";
import syntheseIcon from "../../../assets/img/SYNTHESE.svg";
import exclamationIcon from "../../../assets/img/exclamation.png";
import exportElements from "../../../assets/img/exportElements.png";
import { getMonthEndBalanceSheetItems } from "../../../data/slices/monthEndBalanceSheetItems";
import { exportWritingList, exportMonthEndBalanceSheet, exportSynthesisResult } from "../../../data/slices/exports";
import { useDispatch, useSelector } from "../../../data/store";
import { convertToYYYYMM } from "../../../utilities/filters";
import { formatFR } from "../../../utilities/format";
import Button from "../../Button";
import Overlay from "../../Overlay";
import HeaderOverlay from "../../Project/Header";
import "./index.scss";
import dayjs from "dayjs";

function MonthEndBalanceSheetItemsModal({ open, onCancel, data }) {
  const dispatch = useDispatch();
  const { data: result, status, error } = useSelector(
    (state) => state.monthEndBalanceSheetItems
  );
  const { exportStatus } = useSelector((state) => state.exports);

  const monthLabel = data?.month;
  const projectId = data?.projectId;

  const monthYYYYMM = useMemo(() => {
    if (!monthLabel) {
      return null;
    }
    return convertToYYYYMM(monthLabel);
  }, [monthLabel]);

  const monthEndDate = useMemo(() => {
    if (!monthYYYYMM) {
      return null;
    }
    const [yearStr, monthStr] = monthYYYYMM.split("-");
    const year = parseInt(yearStr, 10);
    const monthNum = parseInt(monthStr, 10);
    const lastDay = new Date(year, monthNum, 0).getDate();
    return `${monthYYYYMM}-${String(lastDay).padStart(2, "0")}`;
  }, [monthYYYYMM]);

  useEffect(() => {
    if (open && projectId && monthYYYYMM) {
      dispatch(getMonthEndBalanceSheetItems({ projectId, month: monthYYYYMM }));
    }
  }, [open, dispatch, projectId, monthYYYYMM]);

  const handleExportMonthEndBalanceItem = (monthEndBalanceItem) => {
    if (!projectId || !monthEndDate) {
      return;
    }
    dispatch(
      exportWritingList({
        projectId,
        tableType: "all",
        mode: "all",
        exportData: {
          startDate: monthEndDate,
          endDate: monthEndDate,
          monthEndBalanceItem,
        },
      })
    );
  };

  const handleExportFullBilan = () => {
    if (!projectId || !monthYYYYMM) {
      return;
    }
    dispatch(
      exportMonthEndBalanceSheet({
        projectId,
        month: monthYYYYMM,
      })
    );
  };

  const handleExportExerciceResult = () => {
    if (!projectId || !monthYYYYMM) {
      return;
    }
    const end = dayjs(monthYYYYMM, "YYYY-MM").startOf("month");
    // 12 last months: e.g. juin 2026 → juillet 2025 … juin 2026
    const start = end.subtract(11, "month");

    dispatch(
      exportSynthesisResult({
        projectId,
        tableType: "categories_and_plan_comptable",
        mode: "compta",
        exportData: {
          startDate: start.format("YYYY-MM"),
          endDate: end.format("YYYY-MM"),
          maxCategoryDepth: 5,
          at: new Date().toISOString(),
          filterOptions: [],
          objective: null,
        },
      })
    );
  };

  const values = result?.values || {};
  const alerts = result?.alerts || {};

  const formatSignedFR = (amount) => {
    const n = Number(amount || 0);
    const abs = Math.abs(n);
    const formatted = formatFR(abs) || "0,00";
    return n < 0 ? `-${formatted}` : formatted;
  };

  // Totals must match the values as displayed in the popup (same sign rules).
  const displayedImmobilisations = -(values.totalFixedAssets || 0);
  const displayedAmortissements = values.totalDepreciation || 0;
  const displayedStocks = values.inventories || 0;
  const thirdPartyBalances = Array.isArray(values.thirdPartyBalances)
    ? values.thirdPartyBalances
    : [];
  const fixedAssetBalances = Array.isArray(values.fixedAssetBalances)
    ? values.fixedAssetBalances
    : [];
  const depreciationBalances = Array.isArray(values.depreciationBalances)
    ? values.depreciationBalances
    : [];
  const inventoryBalances = Array.isArray(values.inventoryBalances)
    ? values.inventoryBalances
    : [];
  const displayedCreances = values.thirdPartyReceivables || 0;
  const displayedResteAEncaisser = values.outstandingReceivables || 0;
  const displayedDispoTreso = values.cashAvailability || 0;
  const displayedChargesAvance = Math.abs(values.prepaidExpenses || 0);

  const displayedReservesTotales = values.totalReserves || 0;
  const displayedProvisions = values.provisions || 0;
  const provisionBalances = Array.isArray(values.provisionBalances)
    ? values.provisionBalances
    : [];
  const displayedEmprunts = values.loans || 0;
  const loanBalances = Array.isArray(values.loanBalances) ? values.loanBalances : [];
  const cashAccountBalances = Array.isArray(values.cashAccountBalances)
    ? values.cashAccountBalances.map((row) => ({
        categoryId: row.accountTresoId,
        categoryName: row.accountTresoName,
        isRoot: false,
        amount: row.amount,
      }))
    : [];
  const fondsAssociatifsBalances = Array.isArray(values.fondsAssociatifsBalances)
    ? values.fondsAssociatifsBalances
    : [];
  const fondsAssociatifsTotal = fondsAssociatifsBalances.reduce(
    (sum, row) => sum + Number(row?.amount || 0),
    0
  );
  const previousCumulativeResults =
    Number(values.totalReserves || 0) -
    Number(values.exerciceResultLast12Months || 0) -
    fondsAssociatifsTotal;
  const displayedDettes = values.thirdPartyPayables || 0;
  const displayedResteADepenser = Math.abs(values.outstandingPayables || 0);
  const displayedProduitsAvance = values.deferredRevenue || 0;

  const getBalanceRowLabel = (row) =>
    row?.isRoot ? `Racine de ${row.categoryName}` : row?.categoryName;

  // Hide "Racine de ..." when the category has no real subcategories to detail.
  const getCategoryBreakdownRows = (balances = []) => {
    const hasSubCategories = balances.some((row) => !row?.isRoot);
    if (!hasSubCategories) {
      return [];
    }
    return balances;
  };

  const isMouvementsInternesRow = (row) =>
    row?.categoryName === "Mouvements Internes";

  const renderThirdPartyBreakdown = (amountKey) => {
    const rowsWithAmount = thirdPartyBalances.filter(
      (row) => Number(row?.[amountKey] || 0) > 0
    );
    const rowsToDisplay = getCategoryBreakdownRows(rowsWithAmount).sort(
      (a, b) => {
        const aIsMouvementsInternes = isMouvementsInternesRow(a);
        const bIsMouvementsInternes = isMouvementsInternesRow(b);
        if (aIsMouvementsInternes === bIsMouvementsInternes) {
          return 0;
        }
        return aIsMouvementsInternes ? -1 : 1;
      }
    );

    if (!rowsToDisplay.length) {
      return null;
    }

    return (
      <div className="month-end-balance-sheet-third-party-breakdown">
        {rowsToDisplay.map((row) => (
          <div
            className={`month-end-balance-sheet-third-party-row${
              row?.isRoot ? " root-row" : ""
            }${isMouvementsInternesRow(row) ? " mouvements-internes-row" : ""}`}
            key={`${amountKey}-${row.categoryId}`}
          >
            <span>
              {isMouvementsInternesRow(row) && (
                <img
                  className="month-end-balance-sheet-alert-icon"
                  src={exclamationIcon}
                  alt="!"
                />
              )}
              {getBalanceRowLabel(row)}
            </span>
            <span>{formatFR(row?.[amountKey] || 0) || "0,00"}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderCategoryBalanceBreakdown = (balances, getAmount = (amount) => amount) => {
    const rowsToDisplay = getCategoryBreakdownRows(balances);
    if (!rowsToDisplay.length) {
      return null;
    }

    return (
      <div className="month-end-balance-sheet-third-party-breakdown">
        {rowsToDisplay.map((row) => (
          <div
            className={`month-end-balance-sheet-third-party-row${
              row?.isRoot ? " root-row" : ""
            }`}
            key={`category-balance-${row.categoryId}`}
          >
            <span>{getBalanceRowLabel(row)}</span>
            <span>{formatSignedFR(getAmount(row?.amount || 0))}</span>
          </div>
        ))}
      </div>
    );
  };

  const totalActif =
    displayedImmobilisations +
    displayedAmortissements +
    displayedStocks +
    displayedCreances +
    displayedResteAEncaisser +
    displayedDispoTreso +
    displayedChargesAvance;

  const totalPassif =
    displayedReservesTotales +
    displayedProvisions +
    displayedEmprunts +
    displayedDettes +
    displayedResteADepenser +
    displayedProduitsAvance;

  if (!open) {
    return null;
  }

  if (status === "loading") {
    return <div>Loading...</div>;
  }

  if (status === "failed") {
    return <div>Error: {error?.message || error}</div>;
  }

  return (
    <Overlay show={open} closable={true}>
      <HeaderOverlay showlogo>
        <div className="element-centred month-end-balance-sheet-modal">
          <div className="month-end-balance-sheet-modal-header">
            <img src={syntheseIcon} alt="synthese" />
            <span className="month-end-balance-sheet-title">
              Bilan en fin de mois :
            </span>
          </div>

          <span
            className="month-end-balance-sheet-month"
            style={{ textTransform: "capitalize" }}
          >
            {monthLabel}
          </span>

          <div className="month-end-balance-sheet-alerts">
            {alerts?.tresorerieHasUnpointedBeforeMonth && (
              <div className="month-end-balance-sheet-alert">
                <img
                  className="month-end-balance-sheet-alert-icon"
                  src={exclamationIcon}
                  alt="!"
                />
                <span>
                  Trésorerie : Il existe des écritures non pointées avant cette
                  date
                </span>
              </div>
            )}
            {alerts?.comptaHasSimulationBeforeMonth && (
              <div className="month-end-balance-sheet-alert">
                <img
                  className="month-end-balance-sheet-alert-icon"
                  src={exclamationIcon}
                  alt="!"
                />
                <span>
                  Résultat : Il existe des écritures de simulation avant cette
                  date
                </span>
              </div>
            )}
            <button
              type="button"
              className="month-end-balance-sheet-export-btn month-end-balance-sheet-export-bilan-btn"
              title="Exporter le bilan"
              disabled={exportStatus === "loading" || status === "loading"}
              onClick={handleExportFullBilan}
            >
              <img src={exportElements} alt="" />
              Exporter le bilan
            </button>
          </div>

          <div className="month-end-balance-sheet-scroll">
            <div className="month-end-balance-sheet-values">
            <div className="month-end-balance-sheet-values-row two-cols">
              <div className="month-end-balance-sheet-value-block">
                <div className="label">Immobilisations et amortissements :</div>
                <div className="value month-end-balance-sheet-multi-values">
                  {(() => {
                    const immobilisations = displayedImmobilisations;
                    const amortissements = displayedAmortissements;
                    const total = immobilisations + amortissements;
                    return (
                      <>
                  <div className="month-end-balance-sheet-multi-values-row month-end-balance-sheet-multi-values-total month-end-balance-sheet-multi-values-total-top">
                    <span />
                    <span className="month-end-balance-sheet-amount">
                      {formatSignedFR(total)}
                    </span>
                  </div>
                  <div className="month-end-balance-sheet-multi-values-row month-end-balance-sheet-immosubline">
                    <span>Immobilisations :</span>
                    <span className="month-end-balance-sheet-amount">
                      {immobilisations < 0 ? "-" : ""}
                      {formatFR(Math.abs(immobilisations)) || "0,00"}
                    </span>
                  </div>
                  {renderCategoryBalanceBreakdown(
                    fixedAssetBalances,
                    (amount) => -amount
                  )}
                  <div className="month-end-balance-sheet-multi-values-row month-end-balance-sheet-immosubline">
                    <span>Amortissements :</span>
                    <span className="month-end-balance-sheet-amount">
                      {amortissements < 0 ? "-" : ""}
                      {formatFR(Math.abs(amortissements)) || "0,00"}
                    </span>
                  </div>
                  {renderCategoryBalanceBreakdown(depreciationBalances)}
                      </>
                    );
                  })()}
                </div>
              </div>
              <div className="month-end-balance-sheet-value-block">
                <div className="label">
                Fonds Propres :
                </div>
                <div className="value month-end-balance-sheet-third-party-value">
                  <span className="month-end-balance-sheet-third-party-total">
                    {formatSignedFR(values.totalReserves || 0)}
                  </span>
                  <div className="month-end-balance-sheet-multi-values-row month-end-balance-sheet-fonds-subline">
                    <span>Résultats précédents cumulés</span>
                    <span className="month-end-balance-sheet-amount">
                      {formatSignedFR(previousCumulativeResults)}
                    </span>
                  </div>
                  <div className="month-end-balance-sheet-multi-values-row month-end-balance-sheet-fonds-subline">
                    <span>Résultat de l'exercice (12 derniers mois) =</span>
                    <span className="month-end-balance-sheet-amount">
                      {formatSignedFR(values.exerciceResultLast12Months || 0)}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="month-end-balance-sheet-export-btn month-end-balance-sheet-export-exercice-btn"
                    title="Exporter le résultat de l'exercice"
                    disabled={exportStatus === "loading" || status === "loading"}
                    onClick={handleExportExerciceResult}
                  >
                    <img src={exportElements} alt="" />
                    Exporter le résultat de l'exercice
                  </button>
                  {fondsAssociatifsTotal !== 0 && (
                    <>
                      <div className="month-end-balance-sheet-multi-values-row month-end-balance-sheet-fonds-subline">
                        <span>Fonds associatifs :</span>
                        <span className="month-end-balance-sheet-amount">
                          {formatSignedFR(fondsAssociatifsTotal)}
                        </span>
                      </div>
                      {renderCategoryBalanceBreakdown(fondsAssociatifsBalances)}
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="month-end-balance-sheet-values-row two-cols">
              <div className="month-end-balance-sheet-value-block">
                <div className="label">Stocks :</div>
                <div className="value month-end-balance-sheet-third-party-value">
                  <span className="month-end-balance-sheet-third-party-total">
                    {formatSignedFR(values.inventories || 0)}
                  </span>
                  {renderCategoryBalanceBreakdown(inventoryBalances)}
                </div>
              </div>
              <div className="month-end-balance-sheet-value-block">
                <div className="label">Provisions :</div>
                <div className="value month-end-balance-sheet-third-party-value">
                  <span className="month-end-balance-sheet-third-party-total">
                    {formatSignedFR(values.provisions || 0)}
                  </span>
                  {renderCategoryBalanceBreakdown(provisionBalances)}
                </div>
              </div>
            </div>

            <div className="month-end-balance-sheet-values-row two-cols">
              <div className="month-end-balance-sheet-value-block" />
              <div className="month-end-balance-sheet-value-block">
                <div className="label">Emprunts :</div>
                <div className="value month-end-balance-sheet-third-party-value">
                  <span className="month-end-balance-sheet-third-party-total">
                    {formatSignedFR(values.loans || 0)}
                  </span>
                  {renderCategoryBalanceBreakdown(loanBalances)}
                </div>
              </div>
            </div>

            <div className="month-end-balance-sheet-values-row two-cols">
              <div className="month-end-balance-sheet-value-block">
                <div className="label">Comptes de tiers (Débiteurs) - créances :</div>
                <div className="value month-end-balance-sheet-third-party-value">
                  <span className="month-end-balance-sheet-third-party-total">
                  {formatFR(displayedCreances || 0) || "0,00"}
                  </span>
                  {renderThirdPartyBreakdown("debtorAmount")}
                </div>
              </div>
              <div className="month-end-balance-sheet-value-block">
                <div className="label">Comptes de tiers (Créditeurs) - dettes :</div>
                <div className="value month-end-balance-sheet-third-party-value">
                  <span className="month-end-balance-sheet-third-party-total">
                  {formatFR(displayedDettes || 0) || "0,00"}
                  </span>
                  {renderThirdPartyBreakdown("creditorAmount")}
                </div>
              </div>
            </div>

            <div className="month-end-balance-sheet-values-row two-cols">
              <div className="month-end-balance-sheet-value-block">
                <div className="label">Reste à encaisser :</div>
                <div className="value month-end-balance-sheet-value-with-export">
                  <button
                    type="button"
                    className="month-end-balance-sheet-export-btn"
                    title="Exporter la liste"
                    disabled={exportStatus === "loading"}
                    onClick={() =>
                      handleExportMonthEndBalanceItem("outstandingReceivables")
                    }
                  >
                    <img src={exportElements} alt="" />
                    Exporter la liste
                  </button>
                  <span>{formatFR(values.outstandingReceivables || 0) || "0,00"}</span>
                </div>
              </div>
              <div className="month-end-balance-sheet-value-block">
                <div className="label">Reste à dépenser :</div>
                <div className="value month-end-balance-sheet-value-with-export">
                  <button
                    type="button"
                    className="month-end-balance-sheet-export-btn"
                    title="Exporter la liste"
                    disabled={exportStatus === "loading"}
                    onClick={() =>
                      handleExportMonthEndBalanceItem("outstandingPayables")
                    }
                  >
                    <img src={exportElements} alt="" />
                    Exporter la liste
                  </button>
                  <span>{formatFR(values.outstandingPayables || 0) || "0,00"}</span>
                </div>
              </div>
            </div>

            <div className="month-end-balance-sheet-values-row two-cols">
              <div className="month-end-balance-sheet-value-block">
                <div className="label">Disponibilités de trésorerie :</div>
                <div className="value month-end-balance-sheet-third-party-value">
                  <span className="month-end-balance-sheet-third-party-total">
                    {formatSignedFR(values.cashAvailability || 0)}
                  </span>
                  {renderCategoryBalanceBreakdown(cashAccountBalances)}
                </div>
              </div>
              <div className="month-end-balance-sheet-value-block" />
            </div>

            <div className="month-end-balance-sheet-values-row two-cols">
              <div className="month-end-balance-sheet-value-block">
                <div className="label">Décaissements réalisés d’avance :</div>
                <div className="value month-end-balance-sheet-value-with-export">
                  <button
                    type="button"
                    className="month-end-balance-sheet-export-btn"
                    title="Exporter la liste"
                    disabled={exportStatus === "loading"}
                    onClick={() =>
                      handleExportMonthEndBalanceItem("prepaidExpenses")
                    }
                  >
                    <img src={exportElements} alt="" />
                    Exporter la liste
                  </button>
                  <span>{formatFR(values.prepaidExpenses || 0) || "0,00"}</span>
                </div>
              </div>
              <div className="month-end-balance-sheet-value-block">
                <div className="label">Encaissements réalisés d’avance :</div>
                <div className="value month-end-balance-sheet-value-with-export">
                  <button
                    type="button"
                    className="month-end-balance-sheet-export-btn"
                    title="Exporter la liste"
                    disabled={exportStatus === "loading"}
                    onClick={() =>
                      handleExportMonthEndBalanceItem("deferredRevenue")
                    }
                  >
                    <img src={exportElements} alt="" />
                    Exporter la liste
                  </button>
                  <span>{formatFR(values.deferredRevenue || 0) || "0,00"}</span>
                </div>
              </div>
            </div>

            <div className="month-end-balance-sheet-values-row two-cols">
              <div className="month-end-balance-sheet-value-block month-end-balance-sheet-totals">
                <div className="label">TOTAL ACTIF =</div>
                <div className="value">{formatSignedFR(totalActif)}</div>
              </div>
              <div className="month-end-balance-sheet-value-block month-end-balance-sheet-totals">
                <div className="label">TOTAL PASSIF =</div>
                <div className="value">{formatSignedFR(totalPassif)}</div>
              </div>
            </div>
            </div>
          </div>

          <div className="verify-inputs-popup">
            <Button text={"Fermer"} onClick={onCancel} className={"action-button"} />
          </div>
        </div>
      </HeaderOverlay>
    </Overlay>
  );
}

export default MonthEndBalanceSheetItemsModal;

